import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

/**
 * IconChip: a decorative icon on a `rounded-xl` tile, the lead-in for a
 * feature, a step or a theme card. `variant` picks the fill:
 *   soft     → a `bg-brand-soft` tile with a `text-brand` glyph (an icon on a
 *              tint needs 3:1, not 4.5:1),
 *   gradient → `bg-gradient-brand` with a `text-brand-foreground` glyph and
 *              `shadow-brand`, for the one chip that should lead.
 * `size` steps the tile `md` size-10, `lg` size-12, `xl` size-14 and the
 * glyph with it. The tile is `aria-hidden`: the heading or text beside it
 * carries the meaning. A caller may pass its own `data-slot`.
 */
const iconChipVariants = cva(
	'flex shrink-0 items-center justify-center rounded-xl border',
	{
		variants: {
			variant: {
				soft: 'bg-brand-soft text-brand border-transparent',
				gradient:
					'bg-gradient-brand text-brand-foreground shadow-brand border-transparent',
			},
			size: {
				md: 'size-10',
				lg: 'size-12',
				xl: 'size-14',
			},
		},
	},
)

type IconChipVariant = VariantProps<typeof iconChipVariants>

function IconChip({
	icon,
	variant = 'soft',
	size = 'md',
	className,
	...props
}: Omit<React.ComponentProps<'div'>, 'children'> & {
	icon: IconName
	variant?: NonNullable<IconChipVariant['variant']>
	size?: NonNullable<IconChipVariant['size']>
}) {
	return (
		<div
			data-slot="icon-chip"
			{...props}
			data-variant={variant}
			aria-hidden="true"
			className={cn(iconChipVariants({ variant, size, className }))}
		>
			<Icon name={icon} size={size} />
		</div>
	)
}

export { IconChip }
